'use client';
import { FC, JSX, useCallback, useEffect, useState } from 'react';

import Modal, { ModalPropTypes } from '@/layouts/Modal';
import Image from 'next/image';
import BlackWhiteLocation from '../../../../public/images/global/BlackWhiteLocation.svg';
import { Button, Svg, Text } from '@/components';
import EditIcon from '../../../../public/images/global/EditIcon.svg';
import { getNearbyStores } from '@/app/actions';
import { storage } from '@/utils/storage';
import { useLocation } from '@/context/locationContext';
import {
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  STORE_REGION_ID_MAP,
  TOKEN_EXPIRY_TIME,
} from '@/constants';
import { twMerge } from 'tailwind-merge';
import { logger } from '@/config';
import { CheckIcon, MagnifyingGlassIcon } from '@/icons';
import { CloseIcon } from '@/icons';
import LocationIcon from '@/icons/LocationIcon';
import { useCleverTap } from '@/context/clevertapContext';
import { setCookie } from 'cookies-next';
import { usePathname } from 'next/navigation';
import { searchParams } from '@/utils/helper';
type PropTypes = ModalPropTypes & {
  redirectTo?: string;
  asGuest?: boolean;
  message?: string;
};

// Add type safety to store data
interface StoreData {
  code: string;
  name: string;
  street: string[];
  stock_id: string;
  wizzy_api_key: string;
  wizzy_store_id: string;
  min_order_value_enable?: string;
  min_order_value_message?: string;
  min_order_value?: string;
  min_order_value_store_pickup?: string;
  [key: string]: string | string[] | undefined; // Add index signature
}

const NonServiceablePopup: FC<PropTypes> = ({ show, onClose }): JSX.Element => {
  const storedAddressString = storage.get('addressString');
  const pathname = usePathname();
  const { pushEvent } = useCleverTap();
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);

  const {
    setShowPickupStore,
    setShowSearchManually,
    getMagentoStoreData,
    setIsDelivery,
    setHeaderLocationTitle,
    setDeliveryType,
    storeCode,
    geolocationOptions,
    setLocationToast,
    defaultLat,
    defaultLng,
  } = useLocation();
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationPermission, setLocationPermission] = useState<
    'granted' | 'denied' | 'prompt' | 'default' | null
  >(null);

  const handleOpenPickupStore = useCallback(() => {
    setShowPickupStore(true);
    onClose();
  }, [setShowPickupStore, onClose]);

  const handleError = useCallback(
    (error: { code: number; message: any }, reject: (arg0: any) => void) => {
      const errorMessage = getErrorMessage(error.code);
      setLocationError(errorMessage);

      if (error.code === 1) {
        pushEvent({
          event: 'GPS Location Permission Query',
          data: {
            'Page URL': window.location.href,
            'Page Name': document.title,
            Permission: 'Denied',
            'Prompt Source': 'Non-Serviceable Dialog',
            Error: error?.message,
          },
        });
        setLocationPermission('denied');
      }
      reject(error);
    },
    [pushEvent]
  );

  const handleStoreSelect = useCallback(
    async (store: StoreData) => {
      try {
        if (!store?.code) {
          throw new Error('Invalid store data');
        }

        let reloadNeeded = false;
        const oldStoreCode = storage.get('store_code') || DEFAULT_STORE_CODE;
        if (oldStoreCode !== store.code) {
          reloadNeeded = true;
        }

        // Validate store data
        const requiredFields = [
          'code',
          'name',
          'stock_id',
          'wizzy_api_key',
          'wizzy_store_id',
        ];
        for (const field of requiredFields) {
          if (!store[field]) {
            throw new Error(`Missing required store field: ${field}`);
          }
        }

        storage.set('deliveryType', 'pickup');
        setDeliveryType('pickup');
        storage.set('store_code', store.code);
        storage.set('stock_id', store?.stock_id || DEFAULT_STOCK_ID);
        storage.set('wizzy_api_key', store?.wizzy_api_key);
        storage.set('wizzy_store_id', store?.wizzy_store_id);
        storage.set('address', store.name);
        setHeaderLocationTitle(store.name);
        storage.set('storeTitle', store.name);
        storage.set(
          'storeDescription',
          `${store.street[0]} ${store.street[1]}`
        );

        const cookiesToSet = [
          { key: 'store_code', value: store.code },
          { key: 'wizzy_api_key', value: store?.wizzy_api_key },
          { key: 'wizzy_store_id', value: store?.wizzy_store_id },
          { key: 'stock_id', value: store?.stock_id || DEFAULT_STOCK_ID },
        ];

        if (store?.min_order_value_enable === '1') {
          const movData = {
            message: store?.min_order_value_message,
            amount: {
              home: store?.min_order_value,
              pickup: store?.min_order_value_store_pickup,
            },
          };
          cookiesToSet.push({
            key: 'mov',
            value: JSON.stringify(movData),
          });
        }

        for (const cookie of cookiesToSet) {
          setCookie(cookie.key, cookie.value, {
            maxAge: TOKEN_EXPIRY_TIME,
          });
        }

        onClose();
        setLocationToast({
          message: 'Your Pickup Store has been changed.',
          deliveryType: 'pickup',
        });

        setTimeout(() => {
          setLocationToast(undefined);
          if (reloadNeeded) {
            window.location.reload();
          }
        }, 2000);
      } catch (error: any) {
        logger.error('Error in handleStoreSelect:', error);
        setLocationToast({
          message: 'Failed to change store. Please try again.',
          deliveryType: 'pickup',
        });
        setTimeout(() => setLocationToast(undefined), 2000);
      }
    },
    [setDeliveryType, setHeaderLocationTitle, onClose, setLocationToast]
  );

  const getErrorMessage = (code: number): string => {
    switch (code) {
      case 1:
        return 'Please allow location access and try again.';
      case 2:
        return 'Position unavailable.';
      case 3:
        return 'Location request timed out.';
      default:
        return 'Unable to retrieve location.';
    }
  };

  const handleSuccess = useCallback(
    (
      position: { coords: { latitude: any; longitude: any; accuracy: any } },
      resolve: (arg0: any) => void
    ) => {
      pushEvent({
        event: 'GPS Location Permission Query',
        data: {
          'Page URL': window.location.href,
          'Page Name': document.title,
          Permission: 'Granted',
          Position: `${position.coords.latitude}, ${position.coords.longitude}`,
          Accuracy: position.coords.accuracy,
          'Prompt Source': 'Non-Serviceable Dialog',
        },
      });
      setLocationPermission('granted');
      resolve(position);
    },
    [pushEvent]
  );

  const handleGetCurrentLocation = useCallback(async () => {
    try {
      setLocationError(null);
      setIsFetchingLocation(true);

      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser.');
      }

      if (locationPermission === 'denied') {
        throw new Error(
          'Location access is blocked. Please enable location access in your browser settings and try again.'
        );
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            position => handleSuccess(position, resolve),
            error => handleError(error, reject),
            geolocationOptions
          );
        }
      );

      const { latitude, longitude } = position.coords;
      await getMagentoStoreData({
        coords: {
          latitude,
          longitude,
          accuracy: position.coords.accuracy,
          skipToast: false,
          intentionalClick: 1,
        },
      });

      pushEvent({
        event: 'GPS Location Fetched',
        data: {
          'Page URL': window.location.href,
          'Page Name': document.title,
          Serviceable: storage.get('serviceable') || false,
          'Store Code': storage.get('store_code') || DEFAULT_STORE_CODE,
          'Prompt Source': 'Non-Serviceable Dialog',
        },
      });
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error.code);
      setLocationError(errorMessage);
      logger.error('Error getting current location:', error);
    } finally {
      setIsFetchingLocation(false);
    }
  }, [
    locationPermission,
    pushEvent,
    handleSuccess,
    handleError,
    getMagentoStoreData,
  ]);

  const handleOpenSearchManually = useCallback(() => {
    setShowSearchManually(true);
    onClose();
  }, [setShowSearchManually, onClose]);

  const fetchStores = useCallback(async () => {
    const data = await getNearbyStores(
      defaultLat ?? undefined,
      defaultLng ?? undefined
    );
    if (data?.store) {
      // Sort stores to show current store first
      const sortedStores = [...data.store].sort((a, b) => {
        if (a.code === storeCode) return -1;
        if (b.code === storeCode) return 1;
        return 0;
      });
      setStores(sortedStores);
      setSelectedStore(sortedStores[0]);
    }
  }, [storeCode]);

  useEffect(() => {
    setIsDelivery(false);
    storage.set('isDelivery', 'false');
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (pathname?.includes('/shop/product/')) {
      const region_id = searchParams.get(
        'region_id'
      ) as keyof typeof STORE_REGION_ID_MAP;
      if (region_id) {
        const store = stores?.find(
          store => store.code === STORE_REGION_ID_MAP?.[region_id]
        );
        if (store) {
          setSelectedStore(store);
        }
      }
    }
  }, [pathname, stores, searchParams]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      closeOnOutsideClick={false}
      showCloseButton={false}
      id='non-serviceable'
      style='transition-all duration-300 lg:translate-x-0 rounded-t-3xl rounded-b-none'>
      <div className='flex flex-col items-center justify-center gap-y-4 max-lg:w-full'>
        <Image src={BlackWhiteLocation} alt='Location' className='' />
        <Text className='-mt-2 px-12 py-0 text-center text-xl font-semibold text-[#38373A]'>
          We're not available in your area yet. Please try a different location
        </Text>

        <div
          className={twMerge(
            'w-full rounded-lg border border-[#38373A]',
            storage.get('serviceable') === 'false' &&
              'border-[#ecbcbf] bg-pale-rose'
          )}>
          <div className='flex flex-row items-center justify-between py-3 pr-2'>
            <div
              className='flex-1 cursor-pointer'
              onClick={handleOpenSearchManually}>
              <button className='flex cursor-pointer flex-row items-center justify-center gap-2'>
                <Text
                  className={twMerge(
                    'cursor-pointer text-base',
                    isFetchingLocation && 'text-gray-400'
                  )}>
                  {isFetchingLocation
                    ? 'Fetching your location...'
                    : storedAddressString &&
                        storedAddressString !== 'undefined,undefined'
                      ? storedAddressString?.length >
                        (typeof window !== 'undefined' &&
                        window.innerWidth < 550
                          ? 20
                          : 36)
                        ? storedAddressString.substring(
                            0,
                            typeof window !== 'undefined' &&
                              window.innerWidth < 550
                              ? 20
                              : 36
                          ) + '...'
                        : storedAddressString
                      : 'Select Location'}
                </Text>
              </button>
            </div>

            <div className='flex flex-row items-center justify-center gap-3'>
              {storedAddressString ? (
                <button onClick={handleOpenSearchManually}>
                  <Svg className='h-5 w-5 cursor-pointer'>
                    <CloseIcon />
                  </Svg>
                </button>
              ) : (
                <button onClick={handleOpenSearchManually}>
                  <Svg className='h-5 w-5 stroke-[#A0383F]'>
                    <MagnifyingGlassIcon />
                  </Svg>
                </button>
              )}
              <div className='h-4 w-px bg-[#cccccd]' />
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleGetCurrentLocation();
                }}>
                <Svg className='mr-1 h-[14px] w-[14px] cursor-pointer stroke-fig'>
                  <LocationIcon />
                </Svg>
              </button>
            </div>
          </div>
        </div>
        {locationError && (
          <Text className='mt-2 text-left text-xs text-fig'>
            {locationError}
          </Text>
        )}
        {!locationError && storedAddressString && !isFetchingLocation && (
          <div className='text-left text-sm'>
            {storage.get('serviceable') === 'true' ? (
              <div className='flex items-center gap-x-1 pl-1'>
                <Svg className='rounded-full bg-fig stroke-white p-1'>
                  <CheckIcon />
                </Svg>
                <Text className='p-0 text-xs text-fig'>
                  We can deliver to your location
                </Text>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                <Text className='p-0 text-xs text-fig'>
                  We're not available in your area yet. Please try a different
                  location or consider pickup from store.
                </Text>
              </div>
            )}
          </div>
        )}
        <div className='my-2 flex items-center justify-center'>
          <div className='h-px w-10 bg-[#cccccd]' />
          <Text className='text-xs font-bold text-[#ababab]'>
            OR OPT IN FOR STORE PICKUP
          </Text>
          <div className='h-px w-10 bg-[#cccccd]' />
        </div>
        <div className='flex flex-col max-lg:flex-1 w-full'>
          {stores.length > 0 ? (
            <div className='max-lg:flex-1'>
              <div className='flex flex-row items-center justify-between rounded-lg border  border-[#cccccd] p-3 text-left'>
                <div className='flex w-full flex-col gap-1 pr-7'>
                  <Text className='px-0 text-sm font-semibold'>
                    {selectedStore.name}
                  </Text>
                  <Text className='px-0 text-xs'>
                    {selectedStore.street_line1}
                    {selectedStore.street_line2 &&
                      `, ${selectedStore.street_line2}`}
                    , {selectedStore.city} {selectedStore.postcode}
                  </Text>
                </div>
                <button
                  onClick={handleOpenPickupStore}
                  className='flex flex-row items-center justify-center gap-2'>
                  <Image src={EditIcon} alt='icon' className='' />
                </button>
              </div>
            </div>
          ) : (
            <div className='base-loading w-full h-20 rounded-lg' />
          )}
          <Button
            label='Continue with Store Pickup'
            className='mt-5 w-full py-3'
            onClick={() => handleStoreSelect(selectedStore)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default NonServiceablePopup;
